import { FieldProps } from '@rjsf/core';
import React from 'react';
import * as _ from 'lodash-es';
import { useIntl, FormattedMessage } from 'lib/intl';
import { useDocumentParams } from '../../../pages';
import { ReliningServices } from '../index';
import { Box } from '../../../components/layout';
import { Button } from '../../../components/form-elements';
import * as Icons from '../../../components/icons';
import { LocaleKey } from '../../../translations';
import { Text } from '../../../components/typography';
import { HelperText } from '../../../components/form-elements/Forms/FieldEditText';
import { Spacer } from '../../../components/Spacer/Spacer';

type ReliningFileData = {
  fileUrl?: string;
  fileName?: string;
  timestamp?: number;
};

const ACCEPTED_FILE_TYPE = 'application/pdf';
const MAX_FILE_SIZE_BYTES = 6 * 1024 * 1024; // 6 MB

/*
 * Reusable PDF upload field shared by every relining installation form
 * (Spray, Liner, Branch and Other). Rendered right after UV Hardening.
 *
 * - Accepts a single PDF file only.
 * - Validates the file type (PDF) and size (<= 6 MB) before uploading.
 * - Stores the file publicly in the documents S3 bucket via the shared
 *   relining upload mechanism and writes the resulting public URL into the
 *   form data so it persists and can be linked from the certificate.
 */
export function ReliningFileUpload(props: FieldProps<ReliningFileData>) {
  const params = useDocumentParams();
  const intl = useIntl();
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [errorKey, setErrorKey] = React.useState<LocaleKey | null>(null);

  const { uploadReliningFile, isLoading } = ReliningServices.useUploadReliningFile({
    onSuccess: (data) => {
      props.onChange({
        ...props.formData,
        fileUrl: data.uploadUrl,
        fileName: data.fileName,
        timestamp: data.timestamp,
      });
    },
  });

  const hasData = !_.isEmpty(props.formData) && Boolean(props.formData?.fileUrl);

  const title = props.uiSchema['ui:title']
    ? intl.formatMessage({ id: props.uiSchema['ui:title'] })
    : '';

  function handleSelectFile() {
    setErrorKey(null);
    inputRef.current?.click();
  }

  function handleFileChange(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    // Reset the input so selecting the same file again re-triggers onChange.
    event.target.value = '';

    if (!file) {
      return;
    }

    if (file.type !== ACCEPTED_FILE_TYPE) {
      setErrorKey('relining.installationDocument.error.fileType');
      return;
    }

    if (file.size > MAX_FILE_SIZE_BYTES) {
      setErrorKey('relining.installationDocument.error.fileSize');
      return;
    }

    setErrorKey(null);
    uploadReliningFile({
      documentId: params.documentId,
      templateId: params.templateId,
      file,
    });
  }

  function handleRemove() {
    setErrorKey(null);
    props.onChange(undefined);
  }

  return (
    <Box sx={{ mt: -2 }}>
      {title && (
        <React.Fragment>
          <Text as="h2" variant="h2">
            {title}
          </Text>

          <Spacer y={3} />
        </React.Fragment>
      )}

      <input
        ref={inputRef}
        type="file"
        accept={ACCEPTED_FILE_TYPE}
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />

      {!hasData && (
        <Button
          disabled={props.disabled}
          isLoading={isLoading}
          prependIcon={(
            <Box sx={{ display: 'inline-flex', mr: -1, color: 'white.50' }}>
              <Icons.FileUpload />
            </Box>
          )}
          type="button"
          onClick={() => handleSelectFile()}
          intlId="relining.installationDocument.uploadButton"
        />
      )}

      {hasData && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 3,
            borderRadius: 2,
            backgroundColor: 'outlinedTextWidget.background',
          }}
        >
          <a
            href={props.formData?.fileUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              color: '#3091F2',
              textDecoration: 'underline',
              overflow: 'hidden',
            }}
          >
            <Icons.Documents />
            <Text as="span" sx={{ wordBreak: 'break-all' }}>
              {props.formData?.fileName
                ?? intl.formatMessage({ id: 'relining.installationDocument.view' })}
            </Text>
          </a>

          {!props.disabled && (
            <button
              type="button"
              onClick={handleRemove}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                display: 'inline-flex',
                color: 'currentColor',
                marginLeft: 8,
              }}
            >
              <Icons.Delete fill="currentColor" />
            </button>
          )}
        </Box>
      )}

      {errorKey && (
        <HelperText isError>
          <FormattedMessage id={errorKey} />
        </HelperText>
      )}
    </Box>
  );
}

